import { useCallback } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import TeamPill from "components/team-pill";
import {
  useReleaseStaffTaskMutation,
  useTakeStaffTaskMutation
} from "services/staff-tasks";
import type { StaffTask } from "services/types/staff-tasks.type";
import { isUnclaimedTeamTask } from "services/types/staff-tasks.type";

import {
  formatWaiting,
  initialsOf,
  staffDisplayName
} from "../../../utils/team-task.utils";

export const ALREADY_TAKEN_MESSAGE =
  "Someone on the team has already taken this task.";
export const NOT_A_MEMBER_MESSAGE =
  "Only a member of the team can take this task.";
export const CANNOT_HAND_BACK_MESSAGE =
  "Only the person on it, a team lead or an administrator can hand it back.";

const errorStatus = (error: unknown): number | undefined =>
  typeof error === "object" && error !== null && "status" in error
    ? Number((error as { status: unknown }).status)
    : undefined;

/**
 * Teams (TEAMS_FEATURE): the one banner at the top of a team task.
 *
 *   Unclaimed · Sent to <team> by Omar A. · waiting 1 h 40 m            [Take it]
 *   (KR) Karen Ruiz is on it · took it 12 min ago · sent to <team>      [Hand back to team]
 *
 * Renders nothing for a task that was never sent to a team.
 */
export default function TeamTaskBanner({ task }: { task: StaffTask }) {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [takeTask, { isLoading: isTaking }] = useTakeStaffTaskMutation();
  const [releaseTask, { isLoading: isReleasing }] =
    useReleaseStaffTaskMutation();

  const say = useCallback(
    (message: string) => {
      dispatch(setCustomMessage(message));
      dispatch(setSnackbarOpen(true));
    },
    [dispatch]
  );

  const handleTake = useCallback(async () => {
    try {
      await takeTask(task.id).unwrap();
    } catch (error) {
      const status = errorStatus(error);
      say(
        status === 400
          ? ALREADY_TAKEN_MESSAGE
          : status === 403
            ? NOT_A_MEMBER_MESSAGE
            : "Could not take the task. Try again."
      );
    }
  }, [say, takeTask, task.id]);

  const handleRelease = useCallback(async () => {
    try {
      await releaseTask(task.id).unwrap();
    } catch (error) {
      say(
        errorStatus(error) === 403
          ? CANNOT_HAND_BACK_MESSAGE
          : "Could not hand the task back. Try again."
      );
    }
  }, [releaseTask, say, task.id]);

  if (!task.team) return null;

  const team = task.team;
  const sender = staffDisplayName(task.created_by);

  if (isUnclaimedTeamTask(task)) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
        data-testid="team-task-banner-unclaimed"
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
          backgroundColor: alpha(theme.palette.warning.main, 0.12)
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
          Unclaimed
        </Typography>
        <Typography
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap"
          }}
        >
          Sent to <TeamPill name={team.name} color={team.color} />
          {sender ? ` by ${sender}` : ""} · waiting{" "}
          {formatWaiting(task.created_at)}
        </Typography>
        <Stack sx={{ flex: 1 }} />
        <LoadingButton
          variant="contained"
          size="large"
          loading={isTaking}
          onClick={handleTake}
          data-testid="team-task-take"
          sx={{ fontSize: 16, px: 2.5 }}
        >
          Take it
        </LoadingButton>
      </Stack>
    );
  }

  const owner = staffDisplayName(task.assignee);
  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      gap={1.5}
      data-testid="team-task-banner-taken"
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.success.main, 0.45)}`,
        backgroundColor: alpha(theme.palette.success.main, 0.1)
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: 13,
          fontWeight: 700,
          bgcolor: alpha(theme.palette.success.main, 0.2),
          color: "success.dark"
        }}
      >
        {initialsOf(owner)}
      </Avatar>
      <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
        {owner} is on it
      </Typography>
      <Typography
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "wrap"
        }}
      >
        {task.team_claimed_at
          ? `took it ${formatWaiting(task.team_claimed_at)} ago · `
          : ""}
        sent to <TeamPill name={team.name} color={team.color} />
      </Typography>
      <Stack sx={{ flex: 1 }} />
      <Button
        variant="outlined"
        size="small"
        onClick={handleRelease}
        disabled={isReleasing}
        data-testid="team-task-release"
      >
        Hand back to team
      </Button>
    </Stack>
  );
}
